import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Section from '../components/Section';
import {BottomNavigator} from '../components/Navbar';
import usePodcasts from '../hooks/usePodcasts';

const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const Icon = ({children, className = ''}) => (
    <span aria-hidden="true" className={`PodcastIcon ${className}`}>{children}</span>
);

const PodcastControls = memo(({audioUrl, episodeId, title}) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [audioError, setAudioError] = useState(false);
    const [playbackAttempted, setPlaybackAttempted] = useState(false);

    useEffect(() => () => {
        audioRef.current?.pause();
    }, []);

    const togglePlayback = useCallback(async () => {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }

        if (audio.paused) {
            try {
                setPlaybackAttempted(true);
                await audio.play();
            } catch (error) {
                setAudioError(true);
            }
        } else {
            audio.pause();
        }
    }, []);

    const seek = useCallback((amount) => {
        const audio = audioRef.current;
        if (audio) {
            audio.currentTime = Math.max(0, Math.min(audio.duration || 0, audio.currentTime + amount));
        }
    }, []);

    const updateProgress = useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            setCurrentTime(audio.currentTime);
            try {
                window.localStorage.setItem(`lavita-podcast-position-${episodeId}`, String(audio.currentTime));
            } catch {
                // Playback remains available when local storage is unavailable.
            }
        }
    }, [episodeId]);

    const updateDuration = useCallback(() => {
        const audio = audioRef.current;
        if (audio && Number.isFinite(audio.duration)) {
            setDuration(audio.duration);
            try {
                const savedPosition = Number(window.localStorage.getItem(`lavita-podcast-position-${episodeId}`));
                if (Number.isFinite(savedPosition) && savedPosition > 0) {
                    audio.currentTime = Math.min(savedPosition, audio.duration);
                    setCurrentTime(audio.currentTime);
                }
            } catch {
                // Playback remains available when local storage is unavailable.
            }
        }
    }, [episodeId]);

    const handleSliderChange = useCallback((event) => {
        const audio = audioRef.current;
        if (audio) {
            audio.currentTime = Number(event.target.value);
        }
    }, []);

    return (
        <div className="PodcastControls">
            <audio
                ref={audioRef}
                preload="none"
                src={audioUrl}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onTimeUpdate={updateProgress}
                onLoadedMetadata={updateDuration}
                onError={() => {
                    if (playbackAttempted) {
                        setAudioError(true);
                    }
                }}
                aria-label={`${title} audio player`}
            />
            <input
                aria-label={`Playback progress for ${title}`}
                className="PodcastControls__progress"
                type="range"
                min="0"
                max={duration || 1}
                value={Math.min(currentTime, duration || 1)}
                onChange={handleSliderChange}
                style={{'--progress': `${duration ? (currentTime / duration) * 100 : 0}%`}}
            />
            <div className="PodcastControls__timestamps">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
            </div>
            <div className="PodcastControls__actions">
                <button type="button" className="PodcastControls__seek" onClick={() => seek(-15)} aria-label="Back 15 seconds">
                    <span aria-hidden="true">≪</span>
                </button>
                <button
                    type="button"
                    className="PodcastControls__play"
                    onClick={togglePlayback}
                    aria-label={isPlaying ? `Pause ${title}` : `Play ${title}`}
                >
                    <Icon>{isPlaying ? 'Ⅱ' : '▶'}</Icon>
                </button>
                <button type="button" className="PodcastControls__seek" onClick={() => seek(15)} aria-label="Forward 15 seconds">
                    <span aria-hidden="true">≫</span>
                </button>
            </div>
            {audioError && <p className="PodcastControls__error">Audio is temporarily unavailable.</p>}
        </div>
    );
});

const PodcastArtwork = memo(({imageUrl, title}) => {
    const [hasArtworkError, setHasArtworkError] = useState(false);

    return (
        <div className="PodcastItem__artwork">
            {imageUrl && !hasArtworkError ? <img src={imageUrl} alt={`Cover art for ${title}`} onError={() => setHasArtworkError(true)} /> : <span aria-hidden="true">L</span>}
        </div>
    );
});

const PodcastItem = memo(({podcast}) => {
    return (
        <article className="PodcastItem">
            <PodcastArtwork imageUrl={podcast.artworkUrl} title={podcast.title} />
            <div className="PodcastItem__content">
                <h2>{podcast.title}</h2>
            </div>
            <PodcastControls audioUrl={podcast.audioUrl} episodeId={podcast.id} title={podcast.title} />
        </article>
    );
});

const PodcastPage = () => {
    const {t} = useTranslation();
    const {categories, loading, error, refresh} = usePodcasts();

    return (
        <div className="Page PodcastPage">
            <Section className="PodcastSection" id="podcast">
                <div className="PodcastSection__heading">
                    <div>
                        <h1>{t('podcast.title')}</h1>
                        <p>{t('podcast.subtitle')}</p>
                    </div>
                    <button type="button" className="PodcastSection__refresh" onClick={refresh} disabled={loading}>
                        {loading ? t('podcast.loading') : t('podcast.refresh')}
                    </button>
                </div>

                {loading && <p className="PodcastSection__status">{t('podcast.loading')}</p>}
                {error && (
                    <div className="PodcastSection__status PodcastSection__status--error">
                        <p>{error}</p>
                        <button type="button" onClick={refresh}>{t('podcast.retry')}</button>
                    </div>
                )}
                {!loading && !error && categories.length === 0 && (
                    <p className="PodcastSection__status">{t('podcast.empty')}</p>
                )}
                {categories.map((category) => (
                    <section className="PodcastCategory" key={category.id} aria-labelledby={`podcast-category-${category.id}`}>
                        <h2 id={`podcast-category-${category.id}`}>{category.title}</h2>
                        <div className="PodcastItems">
                            {category.episodes.map((podcast) => (
                                <PodcastItem key={podcast.id} podcast={podcast} />
                            ))}
                        </div>
                    </section>
                ))}
            </Section>
            <BottomNavigator next={{to: '/news', title: t('navbar.news')}} />
        </div>
    );
};

export default PodcastPage;
